/**
 * Drag internals for KanbanBoard: pure list maths and pointer hit-testing.
 *
 * ReorderableList (wave 4) has the same keyboard model but keeps its drag
 * code inline. These helpers are written to be lifted into a shared
 * `src/internal/drag/` once both have landed.
 */
/** Finds a card by key. Returns null when no column holds it. */
export function locate(columns, key, getKey) {
    for (const column of columns) {
        const index = column.items.findIndex((i) => getKey(i) === key);
        if (index !== -1)
            return { columnId: column.id, index, item: column.items[index] };
    }
    return null;
}
/**
 * Returns a copy of `columns` with the card `key` moved to `to`. The target
 * index is clamped to the column. Unknown cards or columns leave the columns
 * unchanged (the same array is returned), so the move is safe to replay on
 * newer data.
 */
export function moveCard(columns, key, to, getKey) {
    const from = locate(columns, key, getKey);
    const target = columns.find((c) => c.id === to.columnId);
    if (!from || !target)
        return columns;
    return columns.map((column) => {
        if (column.id !== from.columnId && column.id !== to.columnId)
            return column;
        const items = column.items.slice();
        if (column.id === from.columnId)
            items.splice(from.index, 1);
        if (column.id === to.columnId) {
            items.splice(Math.max(0, Math.min(to.index, items.length)), 0, from.item);
        }
        return { ...column, items };
    });
}
/**
 * The column under a pointer: the one whose horizontal span contains `x`.
 * Board columns sit side by side, so the vertical position does not matter.
 */
export function columnAt(columns, x) {
    for (const c of columns)
        if (x >= c.box.left && x <= c.box.right)
            return c.id;
    return null;
}
/**
 * The insertion index for a pointer at `y` among a column's other cards: how
 * many card centres lie above it. The dragged card must be left out of `boxes`.
 */
export function indexAt(boxes, y) {
    let index = 0;
    for (const b of boxes)
        if ((b.top + b.bottom) / 2 < y)
            index++;
    return index;
}
/** Pixels from a scroller's edge at which dragging starts to scroll it. */
export const EDGE = 48;
/**
 * Scroll speed (px per frame) for a pointer near an edge of `box`: negative
 * towards the start, positive towards the end, 0 outside the edge bands.
 */
export function edgeSpeed(start, end, at) {
    if (at < start || at > end)
        return 0;
    if (at < start + EDGE)
        return -Math.ceil(((start + EDGE - at) / EDGE) * 16);
    if (at > end - EDGE)
        return Math.ceil(((at - (end - EDGE)) / EDGE) * 16);
    return 0;
}
//# sourceMappingURL=kanban-board.drag.js.map